import type { ModelsClub } from '@/api/generated-api'

// BAT-1106: the public athlete profile is the club page of a participant with
// participant_type ATHLETE (BAT-1099). The fields below are not in the
// generated client yet.

/** Athlete fields of the public club record (BAT-1099). */
export type AthleteProfileFields = {
  participant_type?: string
  /** The athlete's public name; the full name never reaches the front. */
  display_name?: string
}

export const isAthleteProfile = (club?: ModelsClub | null): boolean =>
  (club as AthleteProfileFields | null | undefined)?.participant_type ===
  'ATHLETE'

/** Display Name, falling back to the record name. */
export const athleteDisplayName = (club: ModelsClub): string =>
  (club as AthleteProfileFields).display_name?.trim() || club.club_name || '—'

export const ATHLETE_VIEWER_FLAGS_KEY = 'athlete_viewer_flags'
export const FAN_GROWTH_KEY = 'club_fan_growth'

/** «Become a fan» flags of the signed-in viewer (BAT-1099). */
export type ViewerFanFlags = {
  /** This athlete is the viewer's favourite. */
  isFavorite: boolean
  /** The viewer is a fan who may pick it (not the owner, a club, an
   * athlete, an agent or an admin). */
  canBecomeFan: boolean
}

export type FanGrowthPeriod = 'week' | 'month' | 'year'

export type FanGrowthBucket = {
  /** UTC start of the day (week, month) or of the month (year). */
  start: string
  joined: number
  left: number
}

export type FanGrowthBar = {
  key: string
  /** Day of the month, or the short month name for the year. */
  label: string
  /** New fans in the bucket. */
  value: number
  /** Share of the tallest bar, 0..1. */
  ratio: number
}

/**
 * Bars of the chart: new fans per bucket. The heading is the month (week,
 * month) or the year (year) of the window, «September – October» when it
 * crosses one, as in the design.
 */
export function buildFanGrowthChart(
  buckets: FanGrowthBucket[],
  period: FanGrowthPeriod,
  locale: string
): { heading: string; bars: FanGrowthBar[]; total: number } {
  const max = buckets.reduce((m, b) => Math.max(m, b.joined), 0)
  const monthFormat = new Intl.DateTimeFormat(locale, {
    month: 'short',
    timeZone: 'UTC'
  })
  const bars = buckets.map(bucket => {
    const date = new Date(bucket.start)
    return {
      key: bucket.start,
      label:
        period === 'year'
          ? monthFormat.format(date)
          : String(date.getUTCDate()),
      value: bucket.joined,
      ratio: max > 0 ? bucket.joined / max : 0
    }
  })
  // The window can cross a month (or a year): then both ends are named.
  const first = buckets.at(0)
  const last = buckets.at(-1)
  const label =
    period === 'year'
      ? (d: Date) => String(d.getUTCFullYear())
      : (d: Date) =>
          new Intl.DateTimeFormat(locale, {
            month: 'long',
            timeZone: 'UTC'
          }).format(d)
  const from = first ? label(new Date(first.start)) : ''
  const to = last ? label(new Date(last.start)) : ''
  const heading = from === to ? to : `${from} – ${to}`
  const total = buckets.reduce((sum, b) => sum + b.joined, 0)
  return { heading, bars, total }
}
